import { useEffect, useMemo, useState } from "react"
import { useRouter } from "next/router"

import { useTransportQueryUtils } from "@/transport"
import { useAuth, useOrganizationList } from "@clerk/nextjs"

import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

import { OverageSettingsVerification } from "../components/OverageSettingsVerification"
import { connectOverageSettings } from "./overageSettingsTransport"
import { PaidOverageReviewRoute } from "./PaidOverageReviewRoute"

type Connection = Awaited<ReturnType<typeof connectOverageSettings>>
interface MembershipRetry {
  attempt: number
  organizationSlug: string
  revalidate: () => Promise<void>
}

export function OverageSettingsRoute() {
  const router = useRouter()
  const { isLoaded: authLoaded, isSignedIn, getToken, userId } = useAuth()
  const { isLoaded: membershipsLoaded, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  })
  const queryUtils = useTransportQueryUtils()
  const organizationSlug = getRouteOrganizationSlug(router.asPath)
  const clerkOrganizationId = userMemberships?.data?.find(
    ({ organization }) => organization.slug === organizationSlug,
  )?.organization.id
  const scope = JSON.stringify([organizationSlug, clerkOrganizationId])
  const [retry, setRetry] = useState<MembershipRetry | null>(null)
  const [settledRetryAttempt, setSettledRetryAttempt] = useState(0)
  const refreshingMemberships =
    retry !== null &&
    retry.organizationSlug === organizationSlug &&
    settledRetryAttempt !== retry.attempt
  const [lookupFailure, setLookupFailure] = useState<string | null>(null)
  const lookupFailed = lookupFailure === organizationSlug
  const [result, setResult] = useState<{
    scope: string
    connection: Connection | null
  } | null>(null)
  const invalidate = useMemo(
    () => () => queryUtils.invalidateResource("billing-overage-settings"),
    [queryUtils],
  )

  useEffect(() => {
    if (!retry || !refreshingMemberships) {
      return
    }
    let current = true
    void retry.revalidate().then(
      () => {
        if (current) {
          setSettledRetryAttempt(retry.attempt)
        }
      },
      () => {
        if (current) {
          setLookupFailure(retry.organizationSlug)
          setSettledRetryAttempt(retry.attempt)
        }
      },
    )
    return () => {
      current = false
    }
  }, [refreshingMemberships, retry])

  useEffect(() => {
    if (
      !membershipsLoaded ||
      refreshingMemberships ||
      !organizationSlug ||
      clerkOrganizationId ||
      !userMemberships?.hasNextPage ||
      userMemberships.isFetching ||
      userMemberships.error ||
      lookupFailed
    ) {
      return
    }
    let current = true
    void Promise.resolve(userMemberships.fetchNext()).catch(() => {
      if (current) {
        setLookupFailure(organizationSlug)
      }
    })
    return () => {
      current = false
    }
  }, [
    clerkOrganizationId,
    lookupFailed,
    membershipsLoaded,
    organizationSlug,
    refreshingMemberships,
    userMemberships,
  ])

  useEffect(() => {
    if (
      !router.isReady ||
      !authLoaded ||
      !isSignedIn ||
      !membershipsLoaded ||
      refreshingMemberships ||
      !organizationSlug ||
      !clerkOrganizationId ||
      userMemberships?.error ||
      lookupFailed
    ) {
      return
    }
    const controller = new AbortController()
    void connectOverageSettings({
      clerkOrganizationId,
      organizationSlug,
      getToken,
      invalidate,
      signal: controller.signal,
    }).then(
      (connection) => {
        if (!controller.signal.aborted) {
          setResult({ scope, connection })
        }
      },
      () => {
        if (!controller.signal.aborted) {
          setResult({ scope, connection: null })
        }
      },
    )
    return () => controller.abort()
  }, [
    authLoaded,
    clerkOrganizationId,
    getToken,
    invalidate,
    isSignedIn,
    lookupFailed,
    membershipsLoaded,
    organizationSlug,
    refreshingMemberships,
    router.isReady,
    scope,
    userMemberships?.error,
  ])

  const failed =
    !refreshingMemberships &&
    (lookupFailed ||
      userMemberships?.error ||
      (router.isReady && !organizationSlug) ||
      (authLoaded && !isSignedIn) ||
      (membershipsLoaded &&
        !clerkOrganizationId &&
        !userMemberships?.hasNextPage &&
        !userMemberships?.isFetching) ||
      (result?.scope === scope && result.connection === null))
  const connection =
    !refreshingMemberships && !failed && result?.scope === scope
      ? result.connection
      : null

  if (connection && authLoaded && isSignedIn && router.isReady) {
    return (
      <PaidOverageReviewRoute
        key={scope}
        identity={
          userId && clerkOrganizationId && organizationSlug
            ? {
                clerkOrganizationId,
                organizationSlug,
                actorId: userId,
                expectedOrganizationId: connection.organizationId,
                getToken,
              }
            : null
        }
        surface="quick_enablement"
        settings={{
          organizationId: connection.organizationId,
          port: connection.port,
        }}
      />
    )
  }
  return (
    <OverageSettingsVerification
      failed={Boolean(failed)}
      onRetry={() => {
        setResult(null)
        if (!organizationSlug || !userMemberships?.revalidate) {
          setLookupFailure(organizationSlug)
          return
        }
        const revalidate = userMemberships.revalidate
        setLookupFailure(null)
        setRetry((value) => ({
          attempt: (value?.attempt ?? 0) + 1,
          organizationSlug,
          revalidate,
        }))
      }}
    />
  )
}
